/**
 * Did the Backend actually confirm this write?
 *
 * Purchase Order and payment-type both decided success with:
 *
 *     if (res === 1 || res?.status === 200 || res?.success || res) { ... }
 *
 * The trailing `|| res` is the defect. Every API wrapper in this app
 * returns `[]` when a call fails -- non-200, network down, timeout -- and
 * **`[]` is truthy in JavaScript**, so a failed write took the success
 * branch and the user was told "บันทึกใบสั่งซื้อใหม่เรียบร้อยแล้ว" for a
 * document that was never written. Refreshing made it vanish.
 *
 * The `else` branch was no better: it called `updateLocalData`, which
 * invented an id (`Math.max(...ids) + 1`), pushed a row into the table as
 * though it had been saved, and showed the same success dialog.
 *
 * What the Backends actually return for these writes:
 *
 *   purchase_order insert/update  1 on success, 0 on failure
 *   purchase_order delete         1, or a 400 carrying a Thai reason
 *   payment/type   insert/update/delete   1
 *   any wrapper failure           []      <- the case that used to pass
 *
 * So: a bare `1`, or an object that says so. Nothing else counts, and a
 * list never counts.
 */

export const isConfirmedWrite = (res: any): boolean => {
    if (res === null || res === undefined) return false;

    // The failure sentinel every wrapper returns. No write endpoint
    // answers with a list, so this can be rejected outright -- it is the
    // exact value that used to slip through as truthy.
    if (Array.isArray(res)) return false;

    // `1` means written. `0` is the Backend saying it did not write.
    if (typeof res === 'number') return res === 1;

    if (typeof res === 'boolean') return res;

    if (typeof res === 'object') {
        const r = res as Record<string, any>;
        if (r.status === 1 || r.status === 200) return true;
        if (r.success === true) return true;
        // A write that reports the row it created has plainly created it.
        if (Number(r.id) > 0) return true;
        if (typeof r.document_no === 'string' && r.document_no.length > 0) return true;
        // An explicit failure status must never be read as success by one
        // of the looser checks above.
        return false;
    }

    // Strings, including '' and 'OK': no Backend here answers that way,
    // and guessing is what caused this bug.
    return false;
};

/**
 * The Backend's own reason for refusing a write, when it gave one.
 *
 * A refusal that carries a message is usually the most useful thing the
 * user can be told -- "ไม่สามารถลบหรือยกเลิกใบสั่งซื้อนี้ได้ เนื่องจากมี
 * รายการรับเข้าสินค้าที่ยืนยันแล้ว (GR...)" says exactly what to do next,
 * where "ลบไม่สำเร็จ" does not. Callers across the app already read
 * `e?.response?.data?.message`; this is that expression in one place.
 *
 * `fallback` is used when the failure carried no response at all, which
 * is what offline, DNS and timeout look like.
 */
export const backendMessage = (e: any, fallback: string): string => {
    const fromResponse = e?.response?.data?.message;
    if (typeof fromResponse === 'string' && fromResponse.trim().length > 0) {
        return fromResponse;
    }
    return fallback;
};


/**
 * The message to show when a delete failed.
 *
 * Backend now answers a deleted-or-never-there record with a specific
 * 404 and a Thai sentence written for the user -- "ไม่พบใบตรวจนับที่
 * ต้องการลบ (3) อาจถูกลบไปแล้ว" -- which tells them what actually
 * happened, where "ลบข้อมูลไม่สำเร็จ" leaves them to guess whether it is
 * their connection, their permissions, or a colleague who got there
 * first.
 *
 * Only a 4xx may replace the generic text, and only through
 * `backendMessage`, which reads the single user-facing `message` field:
 *
 *   404  the not-found contract this exists for
 *   400/409  a business refusal that already says why -- the same thing
 *            purchase-order surfaces today for a confirmed receipt
 *   401  deliberately excluded: the session layer owns that response and
 *        showing its text would talk over a sign-out
 *   5xx, network, timeout, no response at all
 *        generic text only. Those bodies carry server internals, and a
 *        user cannot act on them anyway.
 */
export const deleteFailureMessage = (e: any, fallback: string): string =>
    writeFailureMessage(e, fallback);


/**
 * The same rule, for a write that is not a delete.
 *
 * The policy above was never about deleting -- it is about which failures
 * carry a sentence worth showing. Saving needs it now for the same
 * reason: Backend answers a save aimed at a device someone else already
 * removed with a 404 that names it, and a name sent where an id belongs
 * with a 400, and neither is something "จัดการมูลไม่สำเร็จ" can express.
 *
 * `deleteFailureMessage` stays as it is and delegates here, so every call
 * site that already reads well keeps its name and its behaviour.
 */
export const writeFailureMessage = (e: any, fallback: string): string => {
    const status = e?.response?.status;
    if (typeof status !== 'number') return fallback;
    if (status === 401) return fallback;
    if (status < 400 || status >= 500) return fallback;
    return backendMessage(e, fallback);
};
